import {useEffect} from "react"
import {useSignal} from "@preact/signals-react"
import {useAppToast} from "@ui-seeker/app"
import {getSubmit} from "@ui-seeker/utils"

import {BACKEND_APIS} from "@constants"

/**
 * Tier system configuration
 */
const TIERS = [
	{ level: 1, minMappings: 0, maxMappings: 25000, price: 0.015, name: "Tier 1" },
	{ level: 2, minMappings: 25001, maxMappings: 50000, price: 0.016, name: "Tier 2" },
]

/**
 * Calculate current tier and earnings based on total mappings
 */
const calculateTierInfo = (totalMappings) => {
	let currentTier = TIERS[0]
	let totalEarnings = 0
	let remainingMappings = totalMappings

	// Calculate earnings across tiers
	for (const tier of TIERS) {
		if (remainingMappings <= 0) break

		const tierMappings = Math.min(
			remainingMappings,
			tier.maxMappings - tier.minMappings + 1
		)
		
		totalEarnings += tierMappings * tier.price
		remainingMappings -= tierMappings

		if (totalMappings >= tier.minMappings && totalMappings <= tier.maxMappings) {
			currentTier = tier
		}
	}

	// Calculate progress to next tier
	const nextTier = TIERS.find(t => t.level === currentTier.level + 1)
	const mappingsInCurrentTier = totalMappings - currentTier.minMappings
	const tierRange = currentTier.maxMappings - currentTier.minMappings + 1
	const progressPercentage = (mappingsInCurrentTier / tierRange) * 100
	const mappingsToNextTier = nextTier ? nextTier.minMappings - totalMappings : 0

	return {
		currentTier,
		nextTier,
		totalEarnings,
		progressPercentage,
		mappingsToNextTier,
		mappingsInCurrentTier,
		tierRange
	}
}

/**
 * Hook to fetch user mapping statistics
 * 
 * Expected API response format:
 * {
 *   totalMappings: 150,
 *   pendingMappings: 25,
 *   avgMappingsPerDay: 12.5,
 *   external: true,  // true if external user (shows earnings), false if internal
 *   weeklyData: [
 *     {"date": "Tue", "count": 10},  // 6 days ago
 *     {"date": "Wed", "count": 15},  // 5 days ago
 *     {"date": "Thu", "count": 20},  // 4 days ago
 *     {"date": "Fri", "count": 25},  // 3 days ago
 *     {"date": "Sat", "count": 5},   // 2 days ago
 *     {"date": "Sun", "count": 8},   // yesterday
 *     {"date": "Mon", "count": 12}   // today
 *   ]
 * }
 * 
 * weeklyData should be in chronological order from 6 days ago to today
 */
export default function useUserStats() {
	const { showToast } = useAppToast()
	
	const isLoading = useSignal(true)
	const stats = useSignal({
		totalMappings: 0,
		totalEarnings: 0,
		pendingMappings: 0,
		avgMappingsPerDay: 0,
		weeklyData: [], // Array of {date: string, count: number} in chronological order (last 7 days)
		tierInfo: null,
		isExternal: false
	})

	const fetchStats = () => {
		isLoading.value = true
		getSubmit(`${BACKEND_APIS.MATCHERS}/stats/user-mappings`)
			.then(res => {
				if (res) {
					const totalMappings = res.totalMappings || 0
					const isExternal = res.external || false
					const tierInfo = isExternal ? calculateTierInfo(totalMappings) : null
					
					stats.value = {
						totalMappings,
						totalEarnings: tierInfo ? tierInfo.totalEarnings : 0,
						pendingMappings: res.pendingMappings || 0,
						avgMappingsPerDay: res.avgMappingsPerDay || 0,
						weeklyData: res.weeklyData || [],
						tierInfo,
						isExternal
					}
				}
			})
			.catch(() => showToast.error('Error loading statistics'))
			.finally(() => {
				isLoading.value = false
			})
	}

	useEffect(() => {
		fetchStats()
	}, [])

	return {
		isLoading: isLoading.value,
		stats: stats.value,
		refreshStats: fetchStats
	}
}
